// src/components/steps/ValidateEmail.jsx
import React, { Component, useState } from "react";
import HorizontalTimeline from "../HorizontalTimeline";
import BasketSnippet from "../BasketSnippet";
import EmailApi from "../../api/bff/src/api/EmailApi";
import ApiClient from "../../api/bff/src/ApiClient";
import CustomersApi from "../../api/bff/src/api/CustomersApi";
import config from "../../config/properties.json";
import Space from "../Space";
import { default as content } from '../../content/en'

export default function ValidateEmail({
  next,
  prev,
  userEmail,
  setUserEmail,
  emailValidated,
  setEmailValidated,
  userPlan,
  planPrice,
  customerSelections,
  setCustomerSelections,
  basket,
  affilliateRef,
  setPreSelectedPlan
}) {
  const [email, setEmail] = useState(userEmail || "");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [otpSent, setOtpSent] = useState(emailValidated);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [otpResentCount, setOtpResentCount] = useState(0);
  const [otpError, setOtpError] = useState("");
  const [emailError, setEmailError] = useState("");

  const apiClient = new ApiClient(config.apiBaseUrl);
  const emailApi = new EmailApi(apiClient);
  const customersApi = new CustomersApi(apiClient)

  const temporaryEmailDomains = [
    "@azucore.com",
    "@pbsan.com",
    "@revefr.com",
    "@deltajohnsons.com",
    "@potaance.com",
    "@bltiwd.com",
    "@maildrop.cc",
    "@hidingmail.com",
    "@anowt.com",
    "@cauju.com",
    "@nomail.li",
    "@sharebot.net",
    "@brmail.ch",
    "@myself.com",
    "@guerrillamail.com",
    "@guerrillamailblock.com",
    "@emailax.pro",
    "@ruutukf.com"
  ]


  const handleGenerate = async () => {

    setEmailVerifying(true);

    setEmailValidated(false);

    let lowerCaseEmail;
    //convert the email to lower case to avoid case sensitivity issues
    if (email) {
      lowerCaseEmail = email.toLowerCase();
      setEmail(lowerCaseEmail);
    }

    if (otpSent) {
      setOtpResentCount(otpResentCount + 1);
    }


    // Simple email format validation
    const emailRegex = /^[\w-\.+]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (!emailRegex.test(lowerCaseEmail)) {
      setEmailError("Please enter a valid email address.");
      setEmailVerifying(false);
      return;
    }

    //SB - this is now done in the BFF through a third party library
    // if (temporaryEmailDomains.includes(lowerCaseEmail.substring(lowerCaseEmail.indexOf("@")))) {
    //   setEmailError("Temporary email domains are not permitted. Please use a valid email address.");
    //   setEmailVerifying(false);
    //   return;
    // }

    setEmailError("");

    setUserEmail(lowerCaseEmail);
    // Call the API to send OTP
    console.log("Generating OTP for email:", lowerCaseEmail);

    try {
      const subscriptionInfoResp = await customersApi.customersSubscriptionsPendingGet(lowerCaseEmail)
      console.log('subscriptionInfoResp is: ' + JSON.stringify(subscriptionInfoResp, null, 2))

      if (subscriptionInfoResp && subscriptionInfoResp.orderCompleted) {
        setEmailError("This email address has already been used to sign up to Biker Mobile. Please use a different email.");
        setEmailVerifying(false);
        return
      }
    } catch (error) {
      if (error.status === 404) {
        // No pending subscription found, proceed with OTP generation
        console.log("No pending subscription found for this email, proceeding with OTP generation.");
      } else {
        console.error("Error checking pending subscriptions:", error);
        setEmailError("An error occurred while verifying your email. Please try again later.");
        setEmailVerifying(false);
        return;
      }
    }

    try {
      await emailApi.emailVerifyPost({ email: lowerCaseEmail });
      setOtpSent(true);
      setCustomerSelections({
        ...customerSelections,
        ["Validate Email"]: {
          stepDescription: lowerCaseEmail,
        },
      });
      setOtpError(""); // Clear error when generating new OTP
    } catch (error) {
      console.log('error is: ' + JSON.stringify(error, null, 2))
      setEmailVerifying(false);
      if (error?.body?.code === "TEMPORARY_EMAIL_ADDRESS") {
        setEmailError(content.validateEmail.temporaryEmailDomains);
      } else if (error?.body?.error === "Email already assigned to an order") {
        setEmailError("This email address has already been used to sign up to Biker Mobile. Please use a different email.");
      } else {
        setEmailError("Failed to send OTP. Please try again later.");
      }
    }
  };

  async function validateOtpAndContinue() {
    try {
      //special case where a user has gone back and needs to proceed without revalidating
      if (emailValidated) {
        localStorage.removeItem("confirmedBasketId");
        next();
        return;
      }
      // Call the API to validate OTP
      if (!enteredOtp.trim()) {
        setOtpError("Please enter the OTP sent to your email.");
        return;
      }
      console.log("Validating OTP for email:", email, "with OTP:", enteredOtp);
      const validationResp = await emailApi.emailVerifyPost({ email: email, code: enteredOtp });
      setOtpError(""); // Clear error on success
      setEmailValidated(true);
      localStorage.removeItem("confirmedBasketId");
      next();
    } catch (error) {
      console.error("OTP validation failed:", error);
      setOtpError("Invalid OTP. Please try again."); // Set error message
    }
  }

  async function returnToPrevPage() {
    setPreSelectedPlan(null); //clear pre selected plan to allow customer to change their selection
    prev();
  }

  const isMobile = window.innerWidth <= 450;

  return (
    <>
      <HorizontalTimeline
        customerSelections={customerSelections}
        thisStep={ValidateEmail.name}
      />
      <div className="flex-play">
        <div className="small-card">
          <div className="step-header">
            <h2>{content.validateEmail.h2}</h2>
          </div>
          <div className="small-card-body">
            <input
              type="email"
              placeholder={content.validateEmail.addressPlaceholder}
              tabIndex="1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={emailError ? { borderColor: "red", padding: 12 } : { padding: 12 }}
            />
            {emailError && (
              <div style={{ color: "red", marginTop: 4 }}>{emailError}</div>
            )}
            <button
              onClick={handleGenerate}
              className="button button-secondary"
              disabled={(!email.trim()) || otpSent || emailVerifying}
              tabIndex="2"
              style={{ marginTop: 16 }}
            >
              {content.validateEmail.verifyButton}
            </button>
            <button
              onClick={handleGenerate}
              className="button button-secondary"
              disabled={!otpSent}
              tabIndex="2"
              style={{ marginLeft: 6, marginTop: 16, marginBottom: 16 }}
            >
              {content.validateEmail.resendOtpButton}
            </button>
            {otpSent && (
              <>
                {otpResentCount > 0 && <span style={{ marginLeft: 8 }}>- Resent ({otpResentCount}) times</span>}
                <p>{content.validateEmail.otpSentMessage}</p>
                <input
                  type="text"
                  placeholder={content.validateEmail.otpPlaceholder}
                  tabIndex="3"
                  value={enteredOtp}
                  onChange={(e) => setEnteredOtp(e.target.value)}
                  autoFocus
                  style={otpError ? { borderColor: "red", marginTop: 16 } : { marginTop: 16 }}
                  inputMode="numeric"
                />
                <p style={{ marginTop: 12 }}>If you have a discount code, enter it on the next screen</p>
                {otpError && (
                  <div style={{ color: "red", marginTop: 0 }}>{otpError}</div>
                )}
              </>
            )}
          </div>
          <div className="card-footer">
            <button
              onClick={returnToPrevPage}
              className="button button-secondary"
              tabIndex="5"
            >
              {content.common.back}
            </button>
            {otpSent && (
              <>
                <button
                  onClick={validateOtpAndContinue}
                  className="button button-primary"
                  tabIndex="4"
                >
                  {content.validateEmail.confirmButton}
                </button>
              </>
            )}
          </div>
        </div>
        <BasketSnippet
          customerSelections={customerSelections}
          planPrice={planPrice}
          userPlan={userPlan}
          basket={basket}
        />
      </div>
    </>
  );
}
